import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Badge,
  Flex,
} from "@chakra-ui/react";
import { CheckCircleIcon, LockIcon } from "@chakra-ui/icons";
import axios from "axios";

const GroupSummary = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getGroupKey = (q) => q?.technology ?? "";

  // Fetch questions only. Order is defined by the backend (no client-side sorting).
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/questions`
        );
        setQuestions(response.data || []);
        setLoading(false);
      } catch (err) {
        if (err?.response?.status === 409) {
          navigate(`/evaluate/${uuid}/welcome`, { replace: true });
          return;
        }
        setError(
          "Impossible de charger les questions. Veuillez réessayer plus tard."
        );
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [uuid, navigate]);

  // Build groups from questions in array order (backend order). Within each group, sort by questionNumber.
  const groups = useMemo(() => {
    if (questions.length === 0) return [];
    const seen = {};
    const arr = [];
    questions.forEach((q) => {
      const key = getGroupKey(q);
      if (!seen[key]) {
        seen[key] = { key, technology: q.technology, questions: [] };
        arr.push(seen[key]);
      }
      seen[key].questions.push(q);
    });
    arr.forEach((g) => g.questions.sort((a, b) => (a.questionNumber ?? 0) - (b.questionNumber ?? 0)));
    return arr;
  }, [questions]);

  // Load completed groups from localStorage ---------------------------------
  const completedGroups = useMemo(() => {
    const stored = localStorage.getItem(`completedGroups_${uuid}`);
    try {
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, [uuid]);

  // Determine first incomplete group ----------------------------------------
  const firstIncompleteIndex = groups.findIndex((g) => !completedGroups.includes(g.key));

  // Handlers ----------------------------------------------------------------
  const handleStartGroup = (group) => {
    // Start timer only if not yet started
    if (!localStorage.getItem(`startTime_${uuid}`)) {
      localStorage.setItem(`startTime_${uuid}`, new Date().toISOString());
    }

    // Navigate to first question of selected group
    const firstQ = group.questions[0];
    if (firstQ) {
      navigate(`/evaluate/${uuid}/q/${firstQ.questionNumber}`);
    }
  };

  const handleSubmitEvaluation = () => {
    navigate(`/evaluate/${uuid}/submit`);
  };

  // UI rendering ------------------------------------------------------------
  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" thickness="4px" />
              <Text color="gray.600">Chargement du résumé…</Text>
            </VStack>
          </Center>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <Heading size="md" mt={4} mb={2}>
                Une erreur est survenue
              </Heading>
              <Text mb={4}>{error}</Text>
              <Button colorScheme="red" onClick={() => window.location.reload()}>
                Réessayer
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  const allCompleted = firstIncompleteIndex === -1;

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.lg">
        <Box bg="white" borderRadius="lg" borderWidth="1px" p={8} shadow="sm">
          <VStack spacing={6} align="stretch">
            <Heading size="lg" color="black" display="flex" alignItems="center" gap={2}>
              📋 Progression de l'évaluation
            </Heading>

            <VStack spacing={4} align="stretch">
              {groups.map((group, index) => {
                const completed = completedGroups.includes(group.key);
                const isNext = index === firstIncompleteIndex && !completed;
                return (
                  <Box
                    key={group.key}
                    borderWidth="1px"
                    borderRadius="md"
                    p={4}
                    bg={completed ? "gray.50" : "white"}
                    opacity={completed ? 0.7 : 1}
                    transition="all 0.2s"
                  >
                    <Flex justify="space-between" align="center" gap={4}>
                      <HStack flex="1" spacing={4}>
                        <Text fontWeight="bold" fontSize="lg" color="gray.800" sx={{ marginBottom: "0 !important" }}>
                          {group.technology}
                        </Text>
                        <Badge colorScheme="purple" fontSize="sm">
                          {group.questions.length} question{group.questions.length > 1 ? 's' : ''}
                        </Badge>
                      </HStack>

                      <Box>
                        {completed ? (
                          <HStack spacing={2} color="green.500">
                            <CheckCircleIcon />
                            <Text fontWeight="medium" sx={{ marginBottom: "0 !important" }}>Terminé</Text>
                          </HStack>
                        ) : isNext ? (
                          <Button
                            bg="rgb(37, 87, 167)"
                            color="white"
                            _hover={{ bg: "rgb(27, 67, 137)" }}
                            size="md"
                            onClick={() => handleStartGroup(group)}
                          >
                            Commencer
                          </Button>
                        ) : (
                          <HStack spacing={2} color="gray.400">
                            <LockIcon />
                            <Text fontWeight="medium" sx={{ marginBottom: "0 !important" }}>Verrouillé</Text>
                          </HStack>
                        )}
                      </Box>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>

            {allCompleted && (
              <Button
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                _active={{ bg: "rgb(17, 47, 107)" }}
                size="lg"
                onClick={handleSubmitEvaluation}
                mt={4}
              >
                Soumettre l'évaluation
              </Button>
            )}
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default GroupSummary; 
